import { Injectable } from '@angular/core'
import { Observable, Subscription } from 'rxjs'
import { shareReplay, take } from 'rxjs/operators'
import { Footnote } from 'src/app/models/footnote.model'
import { PcfBasketResponse } from 'src/app/models/portfolio-models/pcf-basket-response.model'
import { PortfolioHoldingResponse } from 'src/app/models/portfolio-models/portfolio-holding-response.model'
import { ProfileResponse } from 'src/app/models/profile-response.model'
import { AEMChunk } from 'src/app/services/AEM/aem-chunk.model'
import { CentralContentService } from 'src/app/services/content/central-content.service'
import { FootnoteService } from 'src/app/services/footnotes/footnotes.service'
import { MainService } from 'src/app/services/main.service'
import { PortfolioHoldingService } from 'src/app/services/portfolio-holding/portfolio-holding.service'
import { TopTenHoldingsService } from 'src/app/services/top-ten-holdings/top-ten-holdings.service'

import { BaseCharacteristic, Portfolio, ProfileData } from './portfolio.model'

const profileDataIndex = 0
const holdingStockIndex = 1
const holdingBondIndex = 2
const holdingShortTermIndex = 3
const holdingCurrencyIndex = 4
const holdingDerivativeIndex = 5
const holdingCommodityIndex = 6
const weightingIndex = 7
const characteristicIndex = 8
const riskIndex = 9
const allocationIndex = 10
const holdingStockDailyIndex = 11
const holdingBondDailyIndex = 12
const holdingShortTermDailyIndex = 13
const holdingDerivativeDailyIndex = 14
const holdingMoneyMarketIndex = 15
const holdingDateRangeIndex = 16
const classificationIndex = 17
const contentIndex = 18

@Injectable({
  providedIn: 'root',
})
export class PortfolioService {
  portfolioModel: Portfolio

  // AEM content Vars
  creditQualityRatings: AEMChunk
  aemContentSub: Subscription
  taxExemptContent: AEMChunk
  taxExemptContentFootNote: any

  sectorContent: any
  // eslint-disable-next-line max-params
  constructor(
    private readonly mainService: MainService,
    private readonly portfolioHoldingService: PortfolioHoldingService,
    private readonly footnoteService: FootnoteService,
    private readonly centralContentService: CentralContentService,
    private readonly topTenHoldingsService: TopTenHoldingsService,
  ) {}
  getPortfolioModel(): Observable<Portfolio> {
    return new Observable((subscriber) => {
      this.mainService
        .getPortfolioObservable()
        .pipe(take(1))
        .subscribe(
          // eslint-disable-next-line complexity
          (dbObs) => {
            if (
              Object.keys(dbObs).length !== 0 &&
              dbObs[0] !== null &&
              dbObs[0].name !== 'HTTPError' &&
              this.portfolioModel === undefined
            ) {
              if (dbObs[profileDataIndex].fundProfile.isExternalFund) {
                this.topTenHoldingsService
                  .getTopTenHoldings(dbObs[profileDataIndex].fundProfile.ticker)
                  .pipe(shareReplay())
                  .subscribe((response) => {
                    this.portfolioModel.topTenHoldings = response
                  })
              }
              this.portfolioModel = {
                profileData: this.parseProfileData(dbObs[profileDataIndex]),
                holdingDetailStock: dbObs[holdingStockIndex],
                holdingDetailBond: dbObs[holdingBondIndex],
                holdingDetailShortTerm: dbObs[holdingShortTermIndex],
                holdingDetailCurrency: dbObs[holdingCurrencyIndex],
                holdingDetailDerivative: dbObs[holdingDerivativeIndex],
                holdingDetailCommodity: dbObs[holdingCommodityIndex],
                weighting: dbObs[weightingIndex],
                characteristic: this.parseCharacteristic(
                  dbObs[characteristicIndex],
                  dbObs[riskIndex]?.volatility,
                ),
                allocation: dbObs[allocationIndex],
                riskResult: dbObs[riskIndex] !== null && dbObs[riskIndex].code,
                levelDesc: dbObs[riskIndex].levelDesc,
                holdingDetailStockDaily: dbObs[holdingStockDailyIndex],
                holdingDetailBondDaily: dbObs[holdingBondDailyIndex],
                holdingDetailShortTermDaily: dbObs[holdingShortTermDailyIndex],
                holdingDetailDerivativeDaily:
                  dbObs[holdingDerivativeDailyIndex],
                holdingDetailMoneyMarket: dbObs[holdingMoneyMarketIndex],
                holdingDetailDateRange: dbObs[holdingDateRangeIndex],
                classification: dbObs[classificationIndex],
                contentBenchmarkFootNote:
                  dbObs[contentIndex]?.benchmarkFootnote,
                contentPortfolioFootNote:
                  dbObs[contentIndex]?.portfolioFootnote,
                contentCreditQualityFootNote:
                  dbObs[contentIndex]?.creditQualityFootnote,
              }
              this.sectorContent = dbObs[contentIndex].footnoteForICBorGICS
              this.setFootnotes()
            }
            subscriber.next(this.portfolioModel)
            subscriber.complete()
          },
        )
    })
  }
  parseProfileData(profileResponse: ProfileResponse): ProfileData {
    return {
      ticker: profileResponse.fundProfile.ticker,
      shortName: profileResponse.fundProfile.shortName,
      is529: profileResponse.fundProfile.is529,
      isETF: profileResponse.fundProfile.isETF,
      isStock: profileResponse.fundProfile.fundFact.isStock,
      isBond: profileResponse.fundProfile.fundFact.isBond,
      isBalanced: profileResponse.fundProfile.fundFact.isBalanced,
      isMutualFund: profileResponse.fundProfile.isMutualFund,
      fundId: profileResponse.fundProfile.fundId,
      isIndex: profileResponse.fundProfile.fundFact.isIndex,
      isStandaloneEtf: profileResponse.fundProfile.fundFact.isStandaloneEtf,
      isMoneyMarket: profileResponse.fundProfile.fundFact.isMoneyMarket,
      isStableValueFund: profileResponse.fundProfile.fundFact.isStableValueFund,
      isTaxExemptMoneyMarketFund:
        profileResponse.fundProfile.fundFact.isTaxExemptMoneyMarketFund,
      isFundOfFunds: profileResponse.fundProfile.fundFact.isFundOfFunds,
      isHybridFund: profileResponse.fundProfile.fundFact.isHybridFund,
      isConvertibleFund: profileResponse.fundProfile.fundFact.isConvertibleFund,
      isMarketNeutralFund:
        profileResponse.fundProfile.fundFact.isMarketNeutralFund,
      isAssetAllocationFund:
        profileResponse.fundProfile.fundFact.isAssetAllocationFund,
      isEtfOfEtfs: profileResponse.fundProfile.fundFact.isEtfOfEtfs,
      hasMoreThan1ShareClass:
        profileResponse.fundProfile.fundFact.hasMoreThan1ShareClass,
      isInternational: profileResponse.fundProfile.fundFact.isInternational,
      isGlobal: profileResponse.fundProfile.fundFact.isGlobal,
      isExternalFund: profileResponse.fundProfile.isExternalFund,
      isTaxExemptBondFund:
        profileResponse.fundProfile.fundFact.isTaxExemptBondFund,
      equityInvestmentStyleId:
        profileResponse?.fundProfile?.equityInvestmentStyleId?.trim(),
      fixedIncomeInvestmentStyleId:
        profileResponse?.fundProfile?.fixedIncomeInvestmentStyleId?.trim(),
      fundCategoryId: profileResponse.fundProfile.fundCategory.low.id,
    }
  }
  /* istanbul ignore next */
  parseCharacteristic(
    characteristic: BaseCharacteristic,
    volatility: any,
  ): BaseCharacteristic {
    characteristic.riskData = {
      betaPrimary: volatility?.betaPrimary,
      rSquaredPrimary: volatility?.rSquaredPrimary,
    }
    return characteristic
  }
  getPcfHoldingData(fundId: string): Observable<PcfBasketResponse> {
    return this.portfolioHoldingService
      .getPortfolioHoldings(fundId, 'pcf', {})
      .pipe(shareReplay()) as Observable<PcfBasketResponse>
  }

  private setFootnotes() {
    if (this.portfolioModel.profileData.isBalanced) {
      if (this.portfolioModel.contentPortfolioFootNote) {
        this.portfolioModel.portfolioFootNote = new Footnote(
          this.portfolioModel.contentPortfolioFootNote,
          'portfolio',
          false,
          'portfolio',
        )
        this.footnoteService.addFootnote(this.portfolioModel.portfolioFootNote)
      }

      if (this.portfolioModel.contentBenchmarkFootNote) {
        this.portfolioModel.benchmarkFootNote = new Footnote(
          this.portfolioModel.contentBenchmarkFootNote,
          'portfolio',
          false,
          'benchmark',
        )
        this.footnoteService.addFootnote(this.portfolioModel.benchmarkFootNote)
      }
    }

    if (this.portfolioModel.contentCreditQualityFootNote) {
      this.portfolioModel.creditQualityFootNote = new Footnote(
        this.portfolioModel.contentCreditQualityFootNote,
        'portfolio',
        false,
        'credit_quality',
      )
      this.footnoteService.addFootnote(
        this.portfolioModel.creditQualityFootNote,
      )
    }

    this.aemContentSub = this.centralContentService.aemContent$.subscribe(
      (content) => {
        if (
          this.portfolioModel.profileData.isBalanced ||
          this.portfolioModel.profileData.isTaxExemptBondFund ||
          (!this.portfolioModel.profileData.isExternalFund &&
            this.portfolioModel.profileData.isMoneyMarket)
        ) {
          this.taxExemptContent = this.centralContentService.getContentByBody(
            'footnote_alternative_minimum_tax',
            content,
          )
          this.taxExemptContentFootNote = new Footnote(
            this.taxExemptContent.contentValue,
            'portfolio',
            true,
            'footnote_alternative_minimum_tax',
          )
          this.footnoteService.addFootnote(this.taxExemptContentFootNote)
        }
      },
    )
    this.setInflationFundFootnote()
    this.setSectorFootnote()
  }

  /* istanbul ignore next */
  private setSectorFootnote() {
    if (
      this.portfolioModel.weighting?.sector?.long[0].item === undefined ||
      this.sectorContent === undefined
    ) {
      return
    } else {
      this.portfolioModel.sectorFootnote = new Footnote(
        this.sectorContent,
        'portfolio',
        false,
        'sector',
      )
      this.footnoteService.addFootnote(this.portfolioModel.sectorFootnote)
    }
  }

  private setInflationFundFootnote() {
    if (
      (this.portfolioModel.profileData.isETF ||
        this.portfolioModel.profileData.isMutualFund) &&
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      this.portfolioModel.profileData.fundCategoryId === 3114
    ) {
      this.portfolioModel.yieldToMaturityFootNote = new Footnote(
        'This figure is an estimated yield-to-maturity (YTM) for the fund. It is calculated by adding ' +
          ' the trailing 12-month inflation adjustment to the "real" (i.e., before inflation) YTM of the ' +
          " fund. Adding the 12-month inflation adjustment allows the fund's yield to be more directly " +
          ' comparable to those of other bond funds. Investors should recognize that the actual YTM ' +
          ' will depend upon the level of inflation experienced going forward.',
        'portfolio',
        false,
        'yield_to_maturity',
      )
      this.footnoteService.addFootnote(
        this.portfolioModel.yieldToMaturityFootNote,
      )
      this.portfolioModel.averageDurationFootNote = new Footnote(
        "The duration estimates the fund's percentage change in price for a given change in the " +
          ' real interest rates in the TIPS market. Historically, the real interest rates on TIPS have' +
          ' been somewhat less volatile than the nominal yields of conventional US Treasury bonds.' +
          ' However, the relationship varies over time and is difficult to predict. There may be periods ' +
          ' when TIPS real yields and prices are more volatile than conventional Treasury bonds.',
        'portfolio',
        false,
        'average_duration',
      )
      this.footnoteService.addFootnote(
        this.portfolioModel.averageDurationFootNote,
      )
    } else {
      return
    }
  }

  getExtraHoldingsData(
    fundId: string,
    type: string,
    viewDaily: string,
  ): Observable<PortfolioHoldingResponse> {
    return this.portfolioHoldingService
      .getPortfolioHoldings(fundId, type, {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        start: 1,
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        count: 20000,
        asOfType: viewDaily,
      })
      .pipe(shareReplay()) as Observable<PortfolioHoldingResponse>
  }
}
